package com.fantasytenis.shared.data.bracket.repository

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.data.bracket.api.BracketApi
import com.fantasytenis.shared.data.bracket.dto.*
import com.fantasytenis.shared.domain.bracket.entity.*
import kotlinx.serialization.json.Json

/**
 * Result wrapper for bracket entry with its picks.
 */
data class BracketEntryWithPicks(
    val entry: BracketEntryEntity,
    val picks: List<BracketPickEntity>
)

/**
 * Bracket Repository Interface.
 *
 * Defines the contract for bracket challenge data operations.
 */
interface BracketRepository {
    // Tournament Draws
    suspend fun getTournamentDraw(drawId: String): Result<TournamentDrawEntity>
    suspend fun listTournamentDraws(
        status: Int? = null,
        gender: String? = null,
        limit: Int = 20,
        offset: Int = 0
    ): Result<List<TournamentDrawEntity>>
    suspend fun getDrawPositions(drawId: String, round: Int? = null): Result<List<DrawPositionEntity>>

    // Bracket Challenges
    suspend fun getBracketChallenge(challengeId: String): Result<BracketChallengeEntity>
    suspend fun listBracketChallenges(
        status: Int? = null,
        featuredOnly: Boolean = false,
        limit: Int = 20,
        offset: Int = 0
    ): Result<List<BracketChallengeEntity>>

    // Bracket Entries
    suspend fun createBracketEntry(challengeId: String, bracketName: String?): Result<BracketEntryEntity>
    suspend fun getBracketEntry(entryId: String): Result<BracketEntryWithPicks>
    suspend fun getUserBracketEntries(challengeId: String? = null): Result<List<BracketEntryEntity>>
    suspend fun submitBracketPicks(entryId: String, picks: List<PickInput>): Result<BracketEntryEntity>
    suspend fun cancelBracketEntry(entryId: String): Result<BracketEntryEntity>

    // Leaderboard
    suspend fun getBracketLeaderboard(
        challengeId: String,
        limit: Int = 100,
        offset: Int = 0
    ): Result<List<BracketEntryEntity>>
}

/**
 * Pick input for submission.
 */
data class PickInput(
    val positionId: String,
    val predictedWinnerId: String
)

/**
 * Bracket Repository Implementation.
 */
class BracketRepositoryImpl(
    private val bracketApi: BracketApi
) : BracketRepository {

    private val json = Json { ignoreUnknownKeys = true }

    // ===== TOURNAMENT DRAWS =====

    override suspend fun getTournamentDraw(drawId: String): Result<TournamentDrawEntity> {
        return when (val response = bracketApi.getTournamentDraw(drawId)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun listTournamentDraws(
        status: Int?,
        gender: String?,
        limit: Int,
        offset: Int
    ): Result<List<TournamentDrawEntity>> {
        return when (val response = bracketApi.listTournamentDraws(status, gender, limit, offset)) {
            is ApiResponse.Success -> Result.success(response.data.map { it.toEntity() })
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getDrawPositions(drawId: String, round: Int?): Result<List<DrawPositionEntity>> {
        return when (val response = bracketApi.getDrawPositions(drawId, round)) {
            is ApiResponse.Success -> Result.success(response.data.map { it.toEntity() })
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    // ===== BRACKET CHALLENGES =====

    override suspend fun getBracketChallenge(challengeId: String): Result<BracketChallengeEntity> {
        return when (val response = bracketApi.getBracketChallenge(challengeId)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun listBracketChallenges(
        status: Int?,
        featuredOnly: Boolean,
        limit: Int,
        offset: Int
    ): Result<List<BracketChallengeEntity>> {
        return when (val response = bracketApi.listBracketChallenges(status, featuredOnly, limit, offset)) {
            is ApiResponse.Success -> Result.success(response.data.map { it.toEntity() })
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    // ===== BRACKET ENTRIES =====

    override suspend fun createBracketEntry(challengeId: String, bracketName: String?): Result<BracketEntryEntity> {
        val request = CreateBracketEntryRequest(bracketName = bracketName)

        return when (val response = bracketApi.createBracketEntry(challengeId, request)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getBracketEntry(entryId: String): Result<BracketEntryWithPicks> {
        return when (val response = bracketApi.getBracketEntry(entryId)) {
            is ApiResponse.Success -> {
                val result = response.data
                if (result.data != null) {
                    Result.success(
                        BracketEntryWithPicks(
                            entry = result.data.toEntity(),
                            picks = result.picks?.map { it.toEntity() } ?: emptyList()
                        )
                    )
                } else {
                    Result.failure(Exception("No entry data returned"))
                }
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getUserBracketEntries(challengeId: String?): Result<List<BracketEntryEntity>> {
        return when (val response = bracketApi.getUserBracketEntries(challengeId)) {
            is ApiResponse.Success -> Result.success(response.data.map { it.toEntity() })
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun submitBracketPicks(entryId: String, picks: List<PickInput>): Result<BracketEntryEntity> {
        val request = SubmitBracketPicksRequest(
            picks = picks.map { PickInputDto(positionId = it.positionId, predictedWinnerId = it.predictedWinnerId) }
        )

        return when (val response = bracketApi.submitBracketPicks(entryId, request)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun cancelBracketEntry(entryId: String): Result<BracketEntryEntity> {
        return when (val response = bracketApi.cancelBracketEntry(entryId)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    // ===== LEADERBOARD =====

    override suspend fun getBracketLeaderboard(
        challengeId: String,
        limit: Int,
        offset: Int
    ): Result<List<BracketEntryEntity>> {
        return when (val response = bracketApi.getBracketLeaderboard(challengeId, limit, offset)) {
            is ApiResponse.Success -> Result.success(response.data.map { it.toEntity() })
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }
}

// ===== DTO TO ENTITY MAPPERS =====

private fun TournamentDrawDto.toEntity(): TournamentDrawEntity {
    return TournamentDrawEntity(
        id = id,
        externalId = externalId,
        tournamentId = tournamentId,
        tournamentName = tournamentName,
        seasonId = seasonId,
        gender = gender,
        drawSize = drawSize,
        status = DrawStatus.fromApiValue(status),
        seedingComplete = seedingComplete,
        startDate = startDate,
        endDate = endDate,
        surface = surface,
        bestOf = bestOf
    )
}

private fun DrawPositionDto.toEntity(): DrawPositionEntity {
    return DrawPositionEntity(
        id = id,
        drawId = drawId,
        round = RoundName.fromApiValue(round) ?: RoundName.R32,
        position = position,
        matchNumber = matchNumber,
        playerId = playerId,
        playerName = playerName,
        playerCountry = playerCountry,
        seed = seed,
        isBye = isBye,
        isQualifier = isQualifier,
        isWildcard = isWildcard,
        isLuckyLoser = isLuckyLoser,
        matchId = matchId,
        opponentPositionId = opponentPositionId,
        winnerId = winnerId,
        matchCompleted = matchCompleted,
        score = score
    )
}

private fun BracketChallengeDto.toEntity(): BracketChallengeEntity {
    val json = Json { ignoreUnknownKeys = true }

    val parsedScoringRules = scoringRules?.let {
        try {
            val dto = json.decodeFromString<ScoringRulesDto>(it)
            ScoringRulesEntity(
                roundPoints = dto.roundPoints ?: ScoringRulesEntity.DEFAULT.roundPoints,
                upsetBonusEnabled = dto.upsetBonusEnabled,
                upsetBonusMultiplier = dto.upsetBonusMultiplier,
                bestOf3Modifier = dto.bestOf3Modifier
            )
        } catch (e: Exception) {
            ScoringRulesEntity.DEFAULT
        }
    } ?: ScoringRulesEntity.DEFAULT

    val parsedPayoutStructure = payoutStructure?.let {
        try {
            val dtos = json.decodeFromString<List<PayoutTierDto>>(it)
            dtos.map { tier ->
                PayoutTierEntity(
                    minRank = tier.minRank,
                    maxRank = tier.maxRank,
                    amount = tier.amount,
                    percentage = tier.percentage
                )
            }
        } catch (e: Exception) {
            emptyList()
        }
    } ?: emptyList()

    return BracketChallengeEntity(
        id = id,
        name = name,
        description = description,
        drawId = drawId,
        tournamentName = tournamentName,
        entryFee = entryFee,
        prizePool = prizePool,
        maxEntries = maxEntries,
        currentEntries = currentEntries,
        maxEntriesPerUser = maxEntriesPerUser,
        lockTime = lockTime,
        status = BracketChallengeStatus.fromApiValue(status),
        scoringRules = parsedScoringRules,
        payoutStructure = parsedPayoutStructure,
        isFeatured = isFeatured,
        createdAt = createdAt,
        lockOverride = LockOverride.fromApiValue(lockOverride),
        lockOverrideBy = lockOverrideBy.ifBlank { null },
        lockOverrideAt = lockOverrideAt.ifBlank { null },
        lockOverrideReason = lockOverrideReason.ifBlank { null }
    )
}

private fun BracketEntryDto.toEntity(): BracketEntryEntity {
    return BracketEntryEntity(
        id = id,
        challengeId = challengeId,
        userId = userId,
        bracketName = bracketName,
        totalPoints = totalPoints,
        correctPicks = correctPicks,
        incorrectPicks = incorrectPicks,
        pendingPicks = pendingPicks,
        possiblePoints = possiblePoints,
        rank = rank,
        payoutAmount = payoutAmount,
        status = BracketEntryStatus.fromApiValue(status),
        submittedAt = submittedAt,
        createdAt = createdAt
    )
}

private fun BracketPickDto.toEntity(): BracketPickEntity {
    return BracketPickEntity(
        id = id,
        entryId = entryId,
        positionId = positionId,
        round = RoundName.fromApiValue(round) ?: RoundName.R32,
        matchNumber = matchNumber,
        predictedWinnerId = predictedWinnerId,
        predictedWinnerName = predictedWinnerName,
        predictedWinnerSeed = predictedWinnerSeed,
        opponentId = opponentId,
        opponentName = opponentName,
        opponentSeed = opponentSeed,
        matchId = matchId,
        actualWinnerId = actualWinnerId,
        isCorrect = isCorrect,
        pointsEarned = pointsEarned,
        isUpsetPick = isUpsetPick,
        upsetBonus = upsetBonus
    )
}
